import React, { useEffect, useMemo, useState } from 'react';
import { Mail, RefreshCw, Save, ShieldCheck } from 'lucide-react';
import { useNotification } from '../components/NotificationProvider';
import {
  AdminApplicationSettings as ApplicationSettings,
  fetchAdminApplicationSettings,
  updateAdminApplicationSettings,
} from '../services/admin.api';
import { useTranslations } from '../translations';
import './AdminApplicationSettings.css';

const AdminApplicationSettings: React.FC = () => {
  const t = useTranslations();
  const labels = t.administration.configuration;
  const { showNotification } = useNotification();
  const [settings, setSettings] = useState<ApplicationSettings | null>(null);
  const [savedSettings, setSavedSettings] = useState<ApplicationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetchAdminApplicationSettings()
      .then((response) => {
        if (!active) return;
        setSettings(response);
        setSavedSettings(response);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [reloadKey]);

  const hasChanges = useMemo(() => Boolean(settings && savedSettings && (
    settings.announcementApprovalEnabled !== savedSettings.announcementApprovalEnabled
    || settings.announcementEmailSendingEnabled !== savedSettings.announcementEmailSendingEnabled
  )), [settings, savedSettings]);

  const updateDraft = (field: 'announcementApprovalEnabled' | 'announcementEmailSendingEnabled') => {
    setSettings((current) => current ? { ...current, [field]: !current[field] } : current);
  };

  const handleSave = async () => {
    if (!settings || !hasChanges || saving) return;
    setSaving(true);
    try {
      const updated = await updateAdminApplicationSettings({
        announcementApprovalEnabled: settings.announcementApprovalEnabled,
        announcementEmailSendingEnabled: settings.announcementEmailSendingEnabled,
      });
      setSettings(updated);
      setSavedSettings(updated);
      showNotification(labels.saveSuccess, 'success');
    } catch (requestError) {
      console.error('Error updating application settings:', requestError);
      showNotification(labels.saveError, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="admin-application-settings-state">
        <RefreshCw className="admin-application-settings-spinner" size={20} aria-hidden />
        {labels.loading}
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="admin-application-settings-state admin-application-settings-error">
        <p>{labels.loadError}</p>
        <button type="button" onClick={() => setReloadKey((current) => current + 1)}>
          <RefreshCw size={17} aria-hidden /> {labels.retry}
        </button>
      </div>
    );
  }

  const settingRows = [
    {
      field: 'announcementApprovalEnabled' as const,
      icon: ShieldCheck,
      title: labels.approvalTitle,
      description: labels.approvalDescription,
      warning: labels.approvalWarning,
    },
    {
      field: 'announcementEmailSendingEnabled' as const,
      icon: Mail,
      title: labels.emailTitle,
      description: labels.emailDescription,
      warning: labels.emailWarning,
    },
  ];
  const updateStatus = settings.updatedAt
    ? labels.lastUpdated.replace('{date}', new Intl.DateTimeFormat('ro-RO', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(settings.updatedAt)))
    : labels.environmentDefaults;

  return (
    <section className="admin-application-settings">
      <header className="admin-application-settings-heading">
        <div>
          <span>{labels.eyebrow}</span>
          <h2>{labels.title}</h2>
          <p>{labels.description}</p>
        </div>
        {hasChanges && <strong>{labels.unsaved}</strong>}
      </header>

      <div className="admin-application-settings-list">
        {settingRows.map(({ field, icon: Icon, title, description, warning }) => {
          const enabled = settings[field];
          return (
            <article className="admin-application-setting" key={field}>
              <span className="admin-application-setting-icon"><Icon size={21} aria-hidden /></span>
              <div className="admin-application-setting-copy">
                <div className="admin-application-setting-title-row">
                  <h3>{title}</h3>
                  <span className={enabled ? 'enabled' : 'disabled'}>
                    {enabled ? labels.enabled : labels.disabled}
                  </span>
                </div>
                <p>{description}</p>
                {!enabled && <small>{warning}</small>}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={title}
                className={`admin-application-setting-switch ${enabled ? 'enabled' : ''}`}
                disabled={saving}
                onClick={() => updateDraft(field)}
              >
                <span aria-hidden />
              </button>
            </article>
          );
        })}
      </div>

      <footer className="admin-application-settings-footer">
        <p>{updateStatus}</p>
        <button type="button" disabled={!hasChanges || saving} onClick={() => void handleSave()}>
          {saving ? <RefreshCw className="admin-application-settings-spinner" size={18} aria-hidden /> : <Save size={18} aria-hidden />}
          {saving ? labels.saving : labels.save}
        </button>
      </footer>
    </section>
  );
};

export default AdminApplicationSettings;
